/**
 * 轻息 H5 与 App Bridge 桥接封装
 * 命名规范：所有方法使用 lowerCamelCase 风格，前缀 tc
 */

import router from '@/router'
import JsBridge from './JsBridge'
import { navigateTo } from '@/utils/navigation'
import { getCurrentSourceQuery } from '@/utils/urlQuery'
import {
  buildVibrateParams,
  DEFAULT_VIBRATION_STYLE,
  isVibrationStyle,
  normalizeVibrateResult,
} from './vibrate'
import type { VibrationStyle } from './vibrate'

export type { VibrationStyle } from './vibrate'

// ==================== 类型定义 ====================

/**
 * App 埋点事件项（传递给客户端的单个事件数据）
 * event 必传，其他字段可选
 */
export interface AppEventItem {
  event: string        // 事件名（必传）
  eventName?: string    // 事件名称（可选）
  pageName?: string     // 页面名称（可选）
  params?: Record<string, unknown>  // 事件参数（可选）
}

/**
 * 1.2.0+ 音频中断 push 类型（端侧 V3.0 协议）
 * - systemInterruption：系统中断开始（来电 / Siri / 闹钟 / 其他 App 抢占）
 * - background：App 切到后台
 */
export type AudioInterruptType = 'systemInterruption' | 'background'
export interface AudioInterruptPayload {
  type: AudioInterruptType
}

declare global {
  interface Window {
    WebViewJavascriptBridge?: any
    WVJBCallbacks?: Array<(bridge: any) => void>
    // Native 调用 H5 的方法
    tcTabChange?: (tabName: string) => void
    tcOnBackControlCallback?: () => void
    tcOnRightButtonCallBack?: () => void
    tcAppOnResume?: () => void
    tcAppOnPause?: () => void
    // tcKeyboardStatus?: (data: { isShow: boolean; keyboardHeight: number }) => void
    tcKeyboardStatus?: (data: string) => void
    // tcOnSwitchChat?: (data: { sessionId: string; new: string }) => void
    tcOnSwitchChat?: (data: string) => void
    // 1.2.0+ 音频中断 push（来电 / 切后台 / 音频焦点丢失 / 系统中断结束）
    // data 形如 { type: 'background' | 'audioFocusLoss' | 'systemInterruption' | 'interruptionEnded' }
    tcOnAudioInterrupt?: (data: string) => void
  }
}

// ==================== 设备检测缓存 ====================

const deviceCache = (() => {
  const ua = navigator.userAgent
  console.info('ua -->', ua)
  // 鸿蒙 WebView UA 含 ArkWeb，需排除后再判断 Android，与 JsBridge.ts 保持一致
  const isHarmony = /ArkWeb/i.test(ua)
  // const isHarmony = true
  return {
    isApp: /(hncyQxApp)/i.test(ua), // 轻息 App 标识
    // isApp: true, // 轻息 App 标识
    isIos: /(iPhone|iPad|iPod|iOS)/i.test(ua),
    isAndroid: /(Android)/i.test(ua) && !isHarmony,
    isHarmony,
  }
})()

// ==================== 环境判断 ====================

export function isIos(): boolean {
  return deviceCache.isIos
}

export function isAndroid(): boolean {
  return deviceCache.isAndroid
}

export function isHarmony(): boolean {
  return deviceCache.isHarmony
}

export function isApp(): boolean {
  return deviceCache.isApp
}

// ==================== JsBridge 核心 ====================

// 注册方法供 Native 调用
export function registerHandler(name: string, fun: (data: any) => any): void {
  JsBridge.registerHandler(name, fun)
}

// 调用 Native 方法
export function callHandler(name: string, data: any, fun?: (data: any) => void): void {
  JsBridge.callHandler(name, data, fun)
}

// ==================== H5监听事件（回调存储）====================

/**
 * 存储业务层注册的回调函数
 * - tcAppOnResume/tcAppOnPause 支持多个监听器（数组）
 * - tcKeyboardStatus 支持多个监听器（数组）
 * - 其他回调仅支持单个监听器
 */
const nativeCallbacks = {
  tcAppOnResume: [] as Array<(res: any) => void>,  // 支持多个监听器
  tcAppOnAppear: [] as Array<(res: any) => void>,  // 支持多个监听器
  tcAppOnPause: [] as Array<(res: any) => void>,   // 支持多个监听器
  tcTabChange: null as ((tabName: string) => void) | null,
  tcOnBackControlCallback: null as (() => void) | null,
  tcOnRightButtonCallBack: null as (() => void) | null,
  // tcKeyboardStatus: null as ((data: { isShow: boolean; keyboardHeight: number }) => void) | null,
  tcKeyboardStatus: [] as Array<(data: string) => void>,  // 支持多个监听器
  // tcOnSwitchChat: null as ((data: { sessionId: string; new: string }) => void) | null,
  tcOnSwitchChat: null as ((data: string) => void) | null,
  // 1.2.0+ 音频中断 push（支持多个监听器）。端格式归一在订阅包装函数内首行完成（见 tcOnAudioInterrupt）
  tcOnAudioInterrupt: [] as Array<(data: any) => void>,
}

let lastTcTabChangeEvent: string | null = null
let lastTcOnSwitchChatEvent: string | null = null

// 生命周期：onResume（支持多个监听器）
export function tcAppOnResume(callback: (res: any) => void): () => void {
  nativeCallbacks.tcAppOnResume.push(callback)
  // 返回取消监听的函数
  return () => {
    const index = nativeCallbacks.tcAppOnResume.indexOf(callback)
    if (index > -1) {
      nativeCallbacks.tcAppOnResume.splice(index, 1)
    }
  }
}

// 生命周期：onAppear（支持多个监听器）这个跟onResume的区别是，onResume初始化不触发，而onAppear初始化也触发
export function tcAppOnAppear(callback: (res: any) => void): () => void {
  nativeCallbacks.tcAppOnAppear.push(callback)
  // 返回取消监听的函数
  return () => {
    const index = nativeCallbacks.tcAppOnAppear.indexOf(callback)
    if (index > -1) {
      nativeCallbacks.tcAppOnAppear.splice(index, 1)
    }
  }
}
// 生命周期：onPause（支持多个监听器）
export function tcAppOnPause(callback: (res: any) => void): () => void {
  nativeCallbacks.tcAppOnPause.push(callback)
  // 返回取消监听的函数
  return () => {
    const index = nativeCallbacks.tcAppOnPause.indexOf(callback)
    if (index > -1) {
      nativeCallbacks.tcAppOnPause.splice(index, 1)
    }
  }
}

// Tab 切换回调
export function tcTabChange(callback: (tabName: string) => void): void {
  nativeCallbacks.tcTabChange = callback
  if (lastTcTabChangeEvent) {
    const tabName = lastTcTabChangeEvent
    lastTcTabChangeEvent = null
    try {
      callback(tabName)
    } catch (error) {
      console.error('[Bridge] tcTabChange replay 回调执行出错:', error)
    }
  }
}

// H5控制原生返回事件回调
export function tcOnBackControlCallback(callback: () => void): void {
  nativeCallbacks.tcOnBackControlCallback = callback
}

// 原生导航右侧功能按钮点击事件回调
export function tcOnRightButtonCallBack(callback: () => void): void {
  nativeCallbacks.tcOnRightButtonCallBack = callback
}

// 监听键盘弹出关闭回调
// @param callback.isShow - 键盘是否已弹出
// @param callback.keyboardHeight - 键盘高度
// @returns 返回取消监听的函数
export function tcKeyboardStatus(callback: (data: string) => void): () => void {
  nativeCallbacks.tcKeyboardStatus.push(callback)
  // 返回取消监听的函数
  return () => {
    const index = nativeCallbacks.tcKeyboardStatus.indexOf(callback)
    if (index > -1) {
      nativeCallbacks.tcKeyboardStatus.splice(index, 1)
    }
  }
}

// 点击某单切换对话
export function tcOnSwitchChat(callback: (data: string) => void): () => void {
  nativeCallbacks.tcOnSwitchChat = callback
  if (lastTcOnSwitchChatEvent) {
    const data = lastTcOnSwitchChatEvent
    lastTcOnSwitchChatEvent = null
    try {
      callback(data)
    } catch (error) {
      console.error('[Bridge] tcOnSwitchChat replay 回调执行出错:', error)
    }
  }
  return () => {
    if (nativeCallbacks.tcOnSwitchChat === callback) {
      nativeCallbacks.tcOnSwitchChat = null
    }
  }
}

/**
 * 1.2.0+ 监听音频中断事件（端侧 V3.0 协议）
 * Native push data：iOS 为 Dictionary，Android/Harmony 为 JSON 字符串
 * type 取值：'systemInterruption' | 'background'
 * 端格式归一在订阅包装函数内首行 normalizeAudioInterrupt 完成（参考 tcOnSwitchChat 业务层自行 parse 思路），
 * 通过 setupNativeCallback('tcOnAudioInterrupt') 走与 tcKeyboardStatus 一致的数组多监听 + raw 透传模式
 * @returns 返回取消监听的函数
 */
function normalizeAudioInterrupt(raw: unknown): AudioInterruptPayload {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as AudioInterruptPayload
    } catch {
      return { type: 'background' }
    }
  }
  if (raw && typeof raw === 'object') {
    return raw as AudioInterruptPayload
  }
  return { type: 'background' }
}

export function tcOnAudioInterrupt(callback: (data: AudioInterruptPayload) => void): () => void {
  if (!deviceCache.isApp) {
    return () => { /* H5 no-op */ }
  }
  // 包装为 (raw)=>cb(normalizeAudioInterrupt(raw)) 后入数组，setupNativeCallback 数组遍历逻辑零改动复用
  const wrapped = (raw: any) => callback(normalizeAudioInterrupt(raw))
  nativeCallbacks.tcOnAudioInterrupt.push(wrapped)
  return () => {
    const idx = nativeCallbacks.tcOnAudioInterrupt.indexOf(wrapped)
    if (idx > -1) nativeCallbacks.tcOnAudioInterrupt.splice(idx, 1)
  }
}

// ==================== 业务方法封装 ====================

/**
 * 1.2.0+ 申请麦克风权限（用于语音快速录入账单）
 * - App 环境：调原生 tcRequestMicPermission（系统弹窗 / 已永久拒绝时由原生弹「无法访问麦克风」二次提示）
 *   端侧 V3.0 协议返回值：iOS 为 Int(1=已授权 / 0=未授权)，Android/Harmony 为 String("1"/"0")
 *   本封装统一归一为 boolean
 * - H5 环境：用 navigator.mediaDevices.getUserMedia({audio:true}) 作为浏览器侧权限触发与判定
 *   立即关闭获取到的 stream，仅用于拿权限态
 * @returns true=已授权 / false=拒绝
 */
export function tcRequestMicPermission(): Promise<boolean> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcRequestMicPermission', '', (res) => {
        // E（v3-fix 复核）：res === '1'(Android/Harmony String) || res === 1(iOS Int) || res === true 已覆盖三端归一，本期确认无需改动
        const ok = res === '1' || res === 1 || res === true
        resolve(ok)
      })
    } else {
      // H5 环境：浏览器原生 getUserMedia 验权
      const mediaDevices = navigator.mediaDevices
      if (!mediaDevices || !mediaDevices.getUserMedia) {
        console.warn('[Bridge] 当前浏览器不支持 getUserMedia，拒绝麦克风权限')
        resolve(false)
        return
      }
      mediaDevices.getUserMedia({ audio: true })
        .then((stream) => {
          // 立刻释放，避免占用麦克风
          stream.getTracks().forEach((t) => t.stop())
          resolve(true)
        })
        .catch((err) => {
          console.warn('[Bridge] H5 getUserMedia 拒绝:', err)
          resolve(false)
        })
    }
  })
}

// 打开相册选择图片
export function tcStartAlbum(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (deviceCache.isApp) {
      // App 环境：调用原生相册
      callHandler('tcStartAlbum', '', (res) => {
        if (res && res.error) {
          reject(new Error(res.error))
        } else {
          resolve(res)
        }
      })
    } else {
      // H5 环境：不支持，应该使用 van-uploader 组件
      reject(new Error('H5 环境请使用 van-uploader 组件'))
    }
  })
}

// 根据URL跳转页面
export function tcJumpActivity(url: string): void {
  if (deviceCache.isApp) {
    callHandler('tcJumpActivity', url)
  } else {
    console.warn('tcJumpActivity 只在 App 环境下生效，H5 环境请使用 navigateTo')
  }
}

// 跳转页面并关闭当前页面
export function tcJumpActivityFinishCurrent(url: string): void {
  if (deviceCache.isApp) {
    callHandler('tcJumpActivityFinishCurrent', url)
  } else {
    console.warn('tcJumpActivityFinishCurrent 只在 App 环境下生效，H5 环境请使用 navigateTo')
  }
}

// 关闭当前页面
export function tcFinishCurrentActivity(): void {
  if (deviceCache.isApp) {
    // App 环境：调用 Native 关闭页面
    callHandler('tcFinishCurrentActivity', '')
  } else {
    // H5 环境或调试模式：使用路由返回
    router.back()
  }
}

export function tcJumpToRootPage(): void {
  if (deviceCache.isApp) {
    callHandler('tcJumpToRootPage', '')
  }
}
// 跳转到首页
export function tcJumpFirstPage(): void {
  if (deviceCache.isApp) {
    callHandler('tcJumpFirstPage', '')
  } else {
    router.replace({ path: '/bill', query: getCurrentSourceQuery() })
  }
}

// 获取登录 token
// 1. App 环境：从客户端通过 bridge 获取 token（原生登录后存储在客户端）
// 2. H5 环境：从 localStorage 获取 token（H5 登录后存储）
export function tcGetLoginInfo(): Promise<string> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      // App 环境：通过 bridge 从客户端获取 token
      callHandler('tcGetLoginInfo', '', (token) => {
        resolve(token || '')
      })
    } else {
      // H5 环境或调试模式：从 localStorage 获取 token
      const token = localStorage.getItem('qx_token') || ''
      resolve(token)
    }
  })
}

// token过期处理
export function tcTokenLoseEfficacy(): void {
  localStorage.clear()
  if (deviceCache.isApp) {
    callHandler('tcTokenLoseEfficacy', '')
  } else {
    // H5 环境：使用 navigateTo 统一处理（自动保留 source 参数）
    navigateTo('/login', { replaceInH5: true })
  }
}

// 获取应用版本信息
export function tcGetAppInfo(): Promise<any> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      // Android / HarmonyOS 原生侧返回 JSON 字符串,需要 parse;iOS 返回 object 可直接用
      if (isAndroid() || isHarmony()) {
        callHandler('tcGetAppInfo', '', (res) => {
          resolve(JSON.parse(res))
        })
      } else {
        callHandler('tcGetAppInfo', '', (res) => {
          resolve(res)
        })
      }
    } else {
      resolve({ version: '0.0.0', platform: 'h5' })
    }
  })
}

/**
 * 保存数据到本地存储（仅 App 环境使用）
 * @param data Dictionary 格式：{ "key1": value1, "key2": value2, ... }
 * @description 支持任意 JSON 值，如果 value 是 JSON 字符串会自动解析为对象
 * @example
 * tcSaveData({ token: 'xxx', userId: 123, userInfo: { name: '张三' } })
 * // Native 接收: { token: 'xxx', userId: 123, userInfo: { name: '张三' } }
 *
 * tcSaveData({ 'user-store': '{"name":"张三"}' })
 * // Native 接收: { 'user-store': { name: '张三' } }  // 自动解析 JSON 字符串
 */
export function tcSaveData(data: Record<string, any>): void {
  if (deviceCache.isApp) {
    const processedData: Record<string, any> = {}
    if (isAndroid()) {
      // 安卓存储数据需要改成 字符串存储
      Object.entries(data).forEach(([key, value]) => {
        processedData[key] = JSON.stringify(value)
      })
    } else {
      // iOS/Harmony 环境：处理数据格式
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === 'string') {
          try {
            processedData[key] = JSON.parse(value)
          } catch {
            processedData[key] = value
          }
        } else {
          processedData[key] = value
        }
      })
    }
    console.info('存储的数据 processedData -->', processedData)
    callHandler('tcSaveData', processedData)
  }
}

/**
 * 获取本地存储数据（仅 App 环境使用）
 * @param keys 单个 key（String）或多个 key（[String]）
 * @returns 返回格式统一为 { "key": value | null }
 * @description 支持任意 JSON 值，如果 value 是对象会自动转为 JSON 字符串（用于 store）
 * @example
 * // 单个获取
 * const result = await tcGetSaveData('token')
 * // result: { "token": "xxx" } 或 { "token": null }
 *
 * // 获取 store（对象会转为字符串）
 * const result = await tcGetSaveData('user-store')
 * // Native 存储: { name: '张三' }
 * // result: { "user-store": '{"name":"张三"}' }  // 自动 stringify
 */
export function tcGetSaveData(keys: string | string[]): Promise<Record<string, any>> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetSaveData', keys, (result: Record<string, any>) => {
        if (!result || Object.keys(result).length === 0) {
          resolve({})
          return
        }
        console.info('获取客户端原始数据 result -->', result)
        const processedData: Record<string, any> = {}
        // 安卓取数据同样需要做一层转化
        if(isAndroid()) {
          const resultData = JSON.parse(result)
          console.info('获取客户端的数据 result -->', resultData)
          Object.entries(resultData).forEach(([key, value]) => {
            console.info('获取客户端的数据 value -->', value)
            processedData[key] = JSON.parse(JSON.stringify(value))
          })
        } else {
          Object.entries(result).forEach(([key, value]) => {
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
              processedData[key] = JSON.stringify(value)
            } else {
              processedData[key] = value
            }
          })
        }
        console.info('获取客户端的数据 processedData -->', processedData)
        resolve(processedData)
      })
    } else {
      resolve({})
    }
  })
}

/**
 * 删除本地存储数据（仅 App 环境使用）
 * @param keys 单个 key（String）或多个 key（[String]）
 * @description 支持单个或批量删除
 * @example
 * // 单个删除
 * tcDeleteData('token')
 *
 * // 批量删除
 * tcDeleteData(['token', 'userId'])
 */
export function tcDeleteData(keys: string | string[]): void {
  if (deviceCache.isApp) {
    callHandler('tcDeleteData', keys)
  }
}

// 显示/隐藏加载动画
export function tcIsShowLoadDialog(isShow: boolean): void {
  if (deviceCache.isApp) {
    callHandler('tcIsShowLoadDialog', isShow)
  }
}

// 显示Toast提示
export function tcShowToast(content: string): void {
  if (deviceCache.isApp) {
    callHandler('tcShowToast', content)
  } else {
    // H5 环境使用 vant 的 showToast
    showToast(content)
  }
}

// 跳转到第三方协议页面
export function tcOtherProtocolJump(url: string): void {
  if (deviceCache.isApp) {
    callHandler('tcOtherProtocolJump', url)
  } else {
    window.open(url, '_blank')
  }
}

// 显示/隐藏tabbar
export function tcIsHiddenTabbar(isShow: boolean): void {
  if (deviceCache.isApp) {
    callHandler('tcIsHiddenTabbar', isShow)
  }
}


// 获取设备平台信息（iOS/Android/HarmonyOS）
export function tcGetDeviceType(): Promise<string> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetDeviceType', '', (res) => {
        resolve(res || '')
      })
    } else {
      resolve('h5')
    }
  })
}

// 获取应用版本号
// 兜底：原生层在某些 App 版本/平台返回非法字符串（如 'UNSUPPORTED' / 空 / undefined），
// 后端 SemVer 格式校验拒会 500（'invalid version format: UNSUPPORTED'），
// 这里前端做白名单格式校验，非法值兜底为 '0.0.0' 让请求走通
export function tcGetAppVersion(): Promise<string> {
  return new Promise((resolve) => {
    const normalize = (raw: unknown): string => {
      if (typeof raw !== 'string') return '0.0.0'
      const trimmed = raw.trim()
      // SemVer 宽松匹配: 主.次.修订（允许后缀 -beta / +build），不匹配走兜底
      if (/^\d+\.\d+(\.\d+)?([.\-+][\w.-]*)?$/.test(trimmed)) return trimmed
      console.warn('[bridge] tcGetAppVersion 返回非法版本号，兜底 0.0.0:', raw)
      return '0.0.0'
    }
    if (deviceCache.isApp) {
      callHandler('tcGetAppVersion', '', (res) => {
        resolve(normalize(res))
      })
    } else {
      resolve('0.0.0')
    }
  })
}

// 获取渠道信息（appstore/vivo/huawei等）
export function tcGetAppChannel(): Promise<string> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetAppChannel', '', (res) => {
        resolve(res || '')
      })
    } else {
      resolve('h5')
    }
  })
}

// 获取用户来源
export function tcGetAppSourceType(): Promise<string> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetAppSourceType', '', (res) => {
        resolve(res || '')
      })
    } else {
      resolve('')
    }
  })
}

// 获取位置信息
export function tcGetAppLocation(): Promise<string> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetAppLocation', '', (res) => {
        resolve(res || '')
      })
    } else {
      resolve('')
    }
  })
}

// H5写入日志文件
export function tcWriteH5Log(logContent: string): void {
  if (deviceCache.isApp) {
    callHandler('tcWriteH5Log', logContent)
  }
}

// 获取状态栏高度
export function tcGetStatusBarHeight(): Promise<number> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetStatusBarHeight', '', (res) => {
        resolve(res || 0)
      })
    } else {
      resolve(0)
    }
  })
}

/**
 * 获取设备唯一 ID
 * @returns Promise<string> 返回设备的唯一标识符（UUID）
 * @description 仅在 App 环境下有效，H5 环境返回空字符串
 * @example
 * const deviceId = await tcGetDeviceId()
 * console.log('设备ID:', deviceId)
 */
export function tcGetDeviceId(): Promise<string> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcGetDeviceId', '', (res) => {
        resolve(res || '')
      })
    } else {
      resolve('')
    }
  })
}

/**
 * 拨打电话
 * @param phoneNumber 手机号码
 * @description 调用系统拨号功能拨打指定电话号码，仅在 App 环境下有效
 * @example
 * tcCallPhone('13800138000')
 */
export function tcCallPhone(phoneNumber: string): void {
  if (deviceCache.isApp) {
    callHandler('tcCallPhone', phoneNumber)
  } else {
    console.warn('拨打电话功能仅在 App 环境下可用')
  }
}

/**
 * 触发 App 原生震动反馈。
 * @param style 可选短触感档位；不传时使用三端默认 medium。
 * @returns 原生是否成功接收并触发震动；普通浏览器或老版本 Native 返回 false。
 */
export function tcVibrate(style?: VibrationStyle): Promise<boolean> {
  if (!deviceCache.isApp) {
    return Promise.resolve(false)
  }

  const params = buildVibrateParams(style)
  const fallbackStyle = isVibrationStyle(params.style) ? params.style : DEFAULT_VIBRATION_STYLE
  return new Promise((resolve) => {
    let settled = false
    const finish = (success: boolean) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      resolve(success)
    }
    const timeoutId = window.setTimeout(() => finish(false), 1000)

    try {
      callHandler('tcVibrate', params, (rawResult) => {
        finish(normalizeVibrateResult(rawResult, fallbackStyle).success)
      })
    } catch (error) {
      console.warn('[Bridge] tcVibrate 调用失败:', error)
      finish(false)
    }
  })
}

// 设置是否由H5控制原生返回事件（1:H5控制, 0:H5不控制)
export function tcSetBackControl(isH5Control: 0 | 1): void {
  if (deviceCache.isApp) {
    callHandler('tcSetBackControl', isH5Control)
  }
}

// 设置原生导航栏右侧按钮
export function tcSetRightButton(config: { show: 0 | 1; iconUrl?: string }): void {
  if (deviceCache.isApp) {
    callHandler('tcSetRightButton', config)
  }
}

// 设置H5边缘弹簧效果 (1:开启, 0:关闭)
export function tcSetBounces(isEnabled: 0 | 1): void {
  if (deviceCache.isApp) {
    callHandler('tcSetBounces', isEnabled)
  }
}

// 设置手势滑动返回 (1:启用, 0:禁用)
export function tcSetSwipeGestureEnabled(isEnabled: 0 | 1): void {
  if (deviceCache.isApp) {
    callHandler('tcSetSwipeGestureEnabled', isEnabled)
  }
}

// 切换原生tab
export function tcChangeTab(tabIndex: string): void {
  if (deviceCache.isApp) {
    callHandler('tcChangeTab', tabIndex)
  }
}

// 调用系统好评功能
export function tcToEvaluate(): 'BRIDGE_INVOKED' | 'BRIDGE_UNAVAILABLE' | 'BRIDGE_INVOKE_ERROR' {
  if (!deviceCache.isApp) {
    console.warn('好评功能仅在 App 环境下可用')
    return 'BRIDGE_UNAVAILABLE'
  }
  try {
    callHandler('tcToEvaluate', '')
    return 'BRIDGE_INVOKED'
  } catch (error) {
    console.error('调用系统好评功能失败:', error)
    return 'BRIDGE_INVOKE_ERROR'
  }
}

// 打开chat页面菜单
export function tcOpenTheMenu(): void {
  if (deviceCache.isApp) {
    callHandler('tcOpenTheMenu', '')
  }
}

/**
 * 埋点上报（由客户端代理 H5 进行埋点上报）
 *
 * @description
 * 将单个埋点事件传递给客户端，由客户端统一处理上报。
 * 适用场景：
 * - webview 快速销毁时，H5 埋点可能丢失
 * - 需要与客户端埋点统一管理
 *
 * @param event 单个埋点事件对象
 * @returns Promise<void>
 *
 * @example
 * ```ts
 * tcEventTracker({
 *   event: 'bill_page_stay',
 *   eventName: '页面停留',
 *   pageName: '对话页',
 *   params: { duration: 5000 },
 * })
 * ```
 */
export function tcEventTracker(event: AppEventItem): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!deviceCache.isApp) {
      console.warn('[Bridge] tcEventTracker 仅在 App 环境下可用')
      reject(new Error('仅在 App 环境下可用'))
      return
    }

    callHandler('tcEventTracker', event, (response: any) => {
      console.log('[Bridge] tcEventTracker 响应:', response)
      resolve()
    })
  })
}


/**
 * 获取当前原生侧是否有原生弹窗弹出，防止原生弹窗和 H5 弹窗重叠
 * @returns Promise<number> 返回状态码：
 *  - 0：未确定，H5 需轮询等待
 *  - 1：当前原生侧正在弹出
 *  - 2：原生侧已经无弹出，可以执行 H5 弹出逻辑
 */
export function tcShowingNativeDialog(): Promise<number> {
  return new Promise((resolve) => {
    if (deviceCache.isApp) {
      callHandler('tcShowingNativeDialog', '', (res) => {
        resolve(res || 0)
      })
    } else {
      resolve(2)
    }
  })
}

// ==================== 挂载到 window 对象（供 Native 调用）====================

/**
 * 注册 Native 回调方法（兼容两种调用方式）
 * - tcAppOnResume/tcAppOnPause 支持多个监听器
 * - 其他回调仅支持单个监听器
 */
function setupNativeCallback<T = any>(name: keyof typeof nativeCallbacks) {
  const callback = (data: T) => {
    console.log(`[Bridge] Native 调用 ${name}:`, data)

    const cb = nativeCallbacks[name]

    // 处理数组类型的回调（tcAppOnResume/tcAppOnPause）
    if (Array.isArray(cb)) {
      cb.forEach(fn => {
        try {
          fn(data as any)
        } catch (error) {
          console.error(`[Bridge] ${name} 回调执行出错:`, error)
        }
      })
    }
    // 处理单个回调
    else if (typeof cb === 'function') {
      try {
        cb(data as any)
      } catch (error) {
        console.error(`[Bridge] ${name} 回调执行出错:`, error)
      }
    } else if (name === 'tcTabChange' && typeof data === 'string' && data.length > 0) {
      lastTcTabChangeEvent = data
      console.log('[Bridge] tcTabChange callback 未注册，暂存最近一次事件:', data)
    } else if (name === 'tcOnSwitchChat' && typeof data === 'string' && data.length > 0) {
      lastTcOnSwitchChatEvent = data
      console.log('[Bridge] tcOnSwitchChat callback 未注册，暂存最近一次事件:', data)
    }
  }

  // 方式1：window 直接调用
  (window as any)[name] = callback

  // 方式2：registerHandler 调用（传递 responseCallback，但由 JsBridge 决定是否调用）
  registerHandler(name, callback)
}

/**
 * 将 H5 监听方法挂载到 window 对象,供 Native 直接调用
 * Native 调用这些方法时,会执行业务层通过对应函数注册的回调
 */
if (deviceCache.isApp) {
  // 批量注册所有回调方法
  setupNativeCallback('tcTabChange')
  setupNativeCallback('tcOnBackControlCallback')
  setupNativeCallback('tcOnRightButtonCallBack')
  setupNativeCallback('tcAppOnResume')
  setupNativeCallback('tcAppOnAppear')
  setupNativeCallback('tcAppOnPause')
  setupNativeCallback('tcKeyboardStatus')
  setupNativeCallback('tcOnSwitchChat')
  setupNativeCallback('tcOnAudioInterrupt')

  // 兼容客户端可能的命名差异（CallBack vs Callback）
  window.tcOnRightButtonCallBack = window.tcOnRightButtonCallBack
}


// ==================== 导出 ====================

export default {
  registerHandler,
  callHandler,
  isApp,
  isIos,
  isAndroid,
  isHarmony,
  tcAppOnResume,
  tcAppOnAppear,
  tcAppOnPause,
  tcTabChange,
  tcOnBackControlCallback,
  tcOnRightButtonCallBack,
  tcKeyboardStatus,
  tcOnSwitchChat,
  tcOnAudioInterrupt,
  tcRequestMicPermission,
  tcJumpActivity,
  tcJumpActivityFinishCurrent,
  tcFinishCurrentActivity,
  tcJumpToRootPage,
  tcJumpFirstPage,
  tcGetLoginInfo,
  tcTokenLoseEfficacy,
  tcGetAppInfo,
  tcSaveData,
  tcDeleteData,
  tcGetSaveData,
  tcIsShowLoadDialog,
  tcShowToast,
  tcOtherProtocolJump,
  tcIsHiddenTabbar,
  tcGetDeviceType,
  tcGetAppVersion,
  tcGetAppChannel,
  tcGetAppSourceType,
  tcGetAppLocation,
  tcWriteH5Log,
  tcGetStatusBarHeight,
  tcSetBackControl,
  tcSetRightButton,
  tcSetBounces,
  tcSetSwipeGestureEnabled,
  tcChangeTab,
  tcToEvaluate,
  tcOpenTheMenu,
  tcStartAlbum,
  tcEventTracker,
  tcShowingNativeDialog,
}
